/**
 * Vidéo d'une ANIMATION (lot 7 du moteur JRAF : YouTube Shorts, TikTok,
 * Reels) — helpers PURS partagés par le worker vidéo (apps/video-worker).
 *
 *   - `animationVideoPayloadSchema` : ce que l'edge fn `animation-video` écrit
 *     dans `youtube_videos.animation_payload` (migration 0673) : le document
 *     de la vidéo (version publiée + voix : la tienne ou la synthèse), la
 *     bande-son (musique, jingles, crédits) et les voix de synthèse posées ;
 *   - `animationCapturePlanSchema` : le plan que renvoie la page de capture
 *     (`/capture/animation` de l'app) — durée, images par seconde, morceaux
 *     de voix et leurs instants, jingles, vignette ;
 *   - `buildAnimationAudioMix` : la piste audio (FFmpeg `-filter_complex`)
 *     assemblée aux instants du plan : voix, musique d'ambiance (montée,
 *     effacée sous les jingles, éteinte au générique — comme dans l'app),
 *     jingles du début et de la fin, niveau final normalisé.
 *
 * Aucune dépendance à FFmpeg ni au système de fichiers : testé.
 */

import { z } from 'zod';

// ------------------------------------------------------------- payload

export const animationSoundSchema = z.object({
  /** URL absolue, ou chemin de l'app (`/animation-audio/jingle-intro.mp3`) */
  url: z.string().min(1).max(600),
  gain: z.number().min(0).max(1),
  durationMs: z.number().int().positive().nullable().optional(),
  label: z.string().nullable().optional(),
  artist: z.string().nullable().optional(),
  license: z.string().nullable().optional(),
  sourceUrl: z.string().nullable().optional(),
});
export type AnimationSound = z.infer<typeof animationSoundSchema>;

/** Volume de la voix : jusqu'à × 3 (une voix enregistrée trop faible). */
export const VOICE_GAIN_MAX = 3;

/** Phases de la méthode H.O.R.A (`scene.hora` du document JRAF). */
export const HORA_PHASES = ['H', 'O', 'R', 'A'] as const;
export type HoraPhase = (typeof HORA_PHASES)[number];

/**
 * Musique propre à une phase : absente = celle de l'animation, `null` = sans
 * musique sur cette phase.
 */
export const musicPhasesSchema = z
  .object({
    H: animationSoundSchema.nullable().optional(),
    O: animationSoundSchema.nullable().optional(),
    R: animationSoundSchema.nullable().optional(),
    A: animationSoundSchema.nullable().optional(),
  })
  .strict();
export type MusicPhases = z.infer<typeof musicPhasesSchema>;

export const animationVideoPayloadSchema = z.object({
  version: z.literal(1),
  /** document JRAF de la vidéo : version publiée + voix (validé par la page de capture) */
  doc: z.record(z.unknown()),
  versionNo: z.number().int().min(1),
  soundtrack: z.object({
    music: animationSoundSchema.nullable(),
    jingleIntro: animationSoundSchema.nullable(),
    jingleOutro: animationSoundSchema.nullable(),
    /** musique propre à une phase H.O.R.A (sinon celle de l'animation) */
    musicPhases: musicPhasesSchema.optional(),
    /** volume de la voix (× 1 = tel qu'enregistrée) ; absent : × 1 */
    voiceGain: z.number().min(0).max(VOICE_GAIN_MAX).optional(),
  }),
  /** parties dites par une voix de synthèse (signalées dans l'admin et la description) */
  synthetic: z.array(z.object({ key: z.string(), label: z.string() })),
  /** durée prévue (ms) */
  durationMs: z.number().int().positive(),
});
export type AnimationVideoPayload = z.infer<typeof animationVideoPayloadSchema>;

// ------------------------------------------------------------ plan reçu

export const animationCapturePlanSchema = z
  .object({
    fps: z.number().int().min(1).max(60),
    width: z.number().int().min(16).max(4096),
    height: z.number().int().min(16).max(4096),
    durationMs: z
      .number()
      .min(1)
      .max(15 * 60_000),
    canvas: z.object({
      width: z.number().int().min(16).max(4096),
      height: z.number().int().min(16).max(4096),
    }),
    clips: z
      .array(
        z.object({
          assetId: z.string(),
          path: z.string().min(1),
          fromMs: z.number().min(0),
          toMs: z.number().min(0),
          atMs: z.number().min(0),
        }),
      )
      .max(5000),
    introMs: z.number().min(0).nullable(),
    outroMs: z.number().min(0).nullable(),
    coverMs: z.number().min(0),
    /** scènes de la vidéo et leurs instants (musique par phase H.O.R.A) */
    scenes: z
      .array(
        z
          .object({
            sceneId: z.string(),
            kind: z.string(),
            startMs: z.number().min(0),
            durationMs: z.number().min(0),
          })
          .passthrough(),
      )
      .max(500)
      .optional(),
  })
  .passthrough();
export type AnimationCapturePlan = z.infer<typeof animationCapturePlanSchema>;

// --------------------------------------------------------------- audio

/** Comme le lecteur de l'app (`soundtrack.ts`). */
export const MIX_MUSIC_FADE_IN_MS = 1500;
export const MIX_MUSIC_FADE_OUT_MS = 2500;
export const MIX_MUSIC_DUCK = 0.35;
export const MIX_DUCK_DOWN_MS = 250;
export const MIX_DUCK_UP_MS = 900;
export const MIX_JINGLE_MS = 2800;
/**
 * Durée maximale d'un jingle (ms), dont un fondu de sortie : un « jingle »
 * qui est en fait un morceau entier ne joue pas sous toute la vidéo.
 * Miroir : JINGLE_MAX_MS (packages/ui, soundtrack.ts).
 */
export const MIX_JINGLE_MAX_MS = 6000;
export const MIX_JINGLE_FADE_MS = 1000;

/** Durée jouée d'un jingle : la sienne, plafonnée. */
export function jinglePlayMs(fileMs: number | null | undefined): number {
  const ms = typeof fileMs === 'number' && fileMs > 0 ? fileMs : MIX_JINGLE_MS;
  return Math.min(ms, MIX_JINGLE_MAX_MS);
}
export const MIX_SAMPLE_RATE = 48_000;

export interface AudioMixInput {
  durationMs: number;
  /** morceaux de voix : fichier local, morceau du fichier, instant dans la vidéo */
  voices: readonly { file: string; fromMs: number; toMs: number; atMs: number }[];
  /** volume de la voix (× 1 par défaut) : plus fort que la musique et les jingles */
  voiceGain?: number;
  music: { file: string; gain: number } | null;
  /**
   * musique propre à des morceaux de la vidéo (phases H.O.R.A) : `music` null
   * = silence sur ce morceau ; ailleurs, la musique de l'animation
   */
  musicPhases?: readonly MusicPhaseRange<{ file: string; gain: number }>[];
  jingles: readonly { file: string; gain: number; atMs: number; durationMs: number }[];
  /** générique : la musique s'éteint */
  outroMs: number | null;
}

export interface AudioMix {
  /** arguments d'entrée FFmpeg, dans l'ordre (`-i …`), numérotés à partir de 0 */
  inputArgs: string[];
  /** nombre d'entrées (la vidéo vient après : entrée n° `inputCount`) */
  inputCount: number;
  filterComplex: string;
  /** étiquette de sortie à `-map` */
  outLabel: string;
}

export interface EnvelopePoint {
  t: number;
  v: number;
}

const round3 = (x: number) => Math.round(x * 1000) / 1000;

/**
 * Volume de la musique dans le temps (points reliés en ligne droite) : monte
 * au début, s'efface sous chaque jingle puis revient, s'éteint au générique.
 */
export function musicEnvelope(input: {
  gain: number;
  durationMs: number;
  jingles: readonly { atMs: number; durationMs: number }[];
  outroMs: number | null;
}): EnvelopePoint[] {
  const g = input.gain;
  const end = input.durationMs;
  const stop = input.outroMs ?? Number.POSITIVE_INFINITY;
  /** volume sans le générique : montée puis jingles */
  const level = (t: number): number => {
    let v = t < MIX_MUSIC_FADE_IN_MS ? (g * t) / MIX_MUSIC_FADE_IN_MS : g;
    for (const j of input.jingles) {
      const a = j.atMs;
      const b = j.atMs + j.durationMs;
      const low = v * MIX_MUSIC_DUCK;
      if (t >= a && t <= b)
        v = t < a + MIX_DUCK_DOWN_MS ? v - (v - low) * ((t - a) / MIX_DUCK_DOWN_MS) : low;
      else if (t > b && t < b + MIX_DUCK_UP_MS) v = low + (v - low) * ((t - b) / MIX_DUCK_UP_MS);
    }
    return v;
  };
  const ts = new Set<number>([0, MIX_MUSIC_FADE_IN_MS, end]);
  for (const j of input.jingles) {
    ts.add(j.atMs);
    ts.add(j.atMs + MIX_DUCK_DOWN_MS);
    ts.add(j.atMs + j.durationMs);
    ts.add(j.atMs + j.durationMs + MIX_DUCK_UP_MS);
  }
  if (Number.isFinite(stop)) {
    ts.add(stop);
    ts.add(stop + MIX_MUSIC_FADE_OUT_MS);
  }
  const points = [...ts]
    .filter((t) => t >= 0 && t <= end)
    .sort((a, b) => a - b)
    .map((t) => {
      let v = level(Math.min(t, stop));
      if (t > stop) v = v * Math.max(0, 1 - (t - stop) / MIX_MUSIC_FADE_OUT_MS);
      return { t, v: round3(v) };
    });
  return points;
}

/** Expression FFmpeg (`volume=…:eval=frame`) d'une enveloppe : `t` en secondes. */
export function envelopeExpr(points: readonly EnvelopePoint[]): string {
  if (points.length === 0) return '0';
  const last = points[points.length - 1]!;
  let expr = String(last.v);
  for (let i = points.length - 2; i >= 0; i--) {
    const a = points[i]!;
    const b = points[i + 1]!;
    const ta = round3(a.t / 1000);
    const tb = round3(b.t / 1000);
    const seg =
      a.v === b.v || tb <= ta
        ? String(a.v)
        : `${a.v}+${round3(b.v - a.v)}*(t-${ta})/${round3(tb - ta)}`;
    expr = `if(lt(t,${tb}),${seg},${expr})`;
  }
  return expr;
}

const ms = (x: number) => Math.max(0, Math.round(x));
const sec = (x: number) => (Math.max(0, x) / 1000).toFixed(3);
const FORMAT = `aresample=${MIX_SAMPLE_RATE},aformat=sample_fmts=fltp:channel_layouts=stereo`;
/** Format de la piste à chaque étape du final : 48 kHz, stéréo, flottants. */
const FINAL = `aformat=sample_fmts=fltp:sample_rates=${MIX_SAMPLE_RATE}:channel_layouts=stereo`;

// ------------------------------------------------- musique par phase H.O.R.A

/** Morceau de la vidéo joué par une phase, et sa musique (`null` : sans). */
export interface MusicPhaseRange<M> {
  phase: HoraPhase;
  fromMs: number;
  toMs: number;
  music: M | null;
}

/** Phase de chaque scène du document (`scenes[].hora`), par identifiant. */
export function horaOfDoc(doc: Record<string, unknown>): Map<string, HoraPhase> {
  const out = new Map<string, HoraPhase>();
  const scenes = Array.isArray(doc.scenes) ? doc.scenes : [];
  for (const s of scenes) {
    if (!s || typeof s !== 'object') continue;
    const { id, hora } = s as { id?: unknown; hora?: unknown };
    if (typeof id === 'string' && (HORA_PHASES as readonly unknown[]).includes(hora))
      out.set(id, hora as HoraPhase);
  }
  return out;
}

/**
 * Morceaux de la vidéo de chaque phase : scènes du plan qui se suivent avec la
 * même phase, réunies. Intro, accroche et générique n'ont pas de phase.
 */
export function phaseRanges(
  scenes: readonly { sceneId: string; startMs: number; durationMs: number }[],
  horaOf: ReadonlyMap<string, HoraPhase>,
): { phase: HoraPhase; fromMs: number; toMs: number }[] {
  const out: { phase: HoraPhase; fromMs: number; toMs: number }[] = [];
  const sorted = [...scenes].sort((a, b) => a.startMs - b.startMs);
  for (const s of sorted) {
    const phase = horaOf.get(s.sceneId);
    if (!phase || s.durationMs <= 0) continue;
    const from = s.startMs;
    const to = s.startMs + s.durationMs;
    const last = out[out.length - 1];
    if (last && last.phase === phase && Math.abs(last.toMs - from) < 1) last.toMs = to;
    else out.push({ phase, fromMs: from, toMs: to });
  }
  return out;
}

/** Morceaux de la vidéo qui reçoivent une musique propre à leur phase. */
export function musicPhaseRanges<M>(
  ranges: readonly { phase: HoraPhase; fromMs: number; toMs: number }[],
  musicOf: Partial<Record<HoraPhase, M | null>> | undefined,
): MusicPhaseRange<M>[] {
  if (!musicOf) return [];
  return ranges
    .filter((r) => musicOf[r.phase] !== undefined)
    .map((r) => ({ ...r, music: musicOf[r.phase] ?? null }));
}

/** Fondu enchaîné entre deux musiques (ms). */
export const MIX_MUSIC_CROSSFADE_MS = 600;

/** Morceau de musique à poser : fichier, volume, instants dans la vidéo. */
export interface MusicPiece {
  file: string;
  gain: number;
  fromMs: number;
  toMs: number;
}

/**
 * Musique de toute la vidéo : celle de chaque phase sur son morceau, celle de
 * l'animation ailleurs ; sans musique là où elle est retirée. Morceaux dans
 * l'ordre, sans chevauchement (le fondu enchaîné est posé par le mixage).
 */
export function musicPieces(input: {
  durationMs: number;
  music: { file: string; gain: number } | null;
  phases: readonly MusicPhaseRange<{ file: string; gain: number }>[];
}): MusicPiece[] {
  const end = input.durationMs;
  const phases = input.phases
    .map((p) => ({ ...p, fromMs: Math.max(0, p.fromMs), toMs: Math.min(end, p.toMs) }))
    .filter((p) => p.toMs > p.fromMs)
    .sort((a, b) => a.fromMs - b.fromMs);
  const out: MusicPiece[] = [];
  const push = (m: { file: string; gain: number } | null, fromMs: number, toMs: number) => {
    if (!m || m.gain <= 0 || toMs - fromMs < 1) return;
    const last = out[out.length - 1];
    // même musique qui continue : un seul morceau, sans fondu
    if (last && last.file === m.file && last.gain === m.gain && Math.abs(last.toMs - fromMs) < 1)
      last.toMs = toMs;
    else out.push({ file: m.file, gain: m.gain, fromMs, toMs });
  };
  let t = 0;
  for (const p of phases) {
    if (p.fromMs > t) push(input.music, t, p.fromMs);
    const from = Math.max(t, p.fromMs);
    push(p.music, from, p.toMs);
    t = Math.max(t, p.toMs);
  }
  if (t < end) push(input.music, t, end);
  return out;
}

/**
 * Piste audio de la vidéo : chaque morceau de voix à son instant, la
 * musique et les jingles, la durée exacte de la vidéo, niveau normalisé.
 */
export function buildAnimationAudioMix(input: AudioMixInput): AudioMix {
  const inputArgs: string[] = [];
  const parts: string[] = [];
  const buses: string[] = [];
  let n = 0;
  const durS = sec(input.durationMs);

  // voix : une entrée par fichier, découpée en morceaux (asplit)
  const byFile = new Map<string, AudioMixInput['voices'][number][]>();
  for (const v of input.voices) {
    if (v.toMs - v.fromMs < 1) continue;
    byFile.set(v.file, [...(byFile.get(v.file) ?? []), v]);
  }
  let k = 0;
  const vg = input.voiceGain ?? 1;
  const voiceVolume =
    Number.isFinite(vg) && vg >= 0 && Math.abs(vg - 1) > 1e-6
      ? `,volume=${round3(Math.min(VOICE_GAIN_MAX, vg))}`
      : '';
  for (const [file, clips] of byFile) {
    const idx = n++;
    inputArgs.push('-i', file);
    const labels = clips.map((_, j) => `v${idx}s${j}`);
    if (clips.length > 1)
      parts.push(`[${idx}:a]asplit=${clips.length}${labels.map((l) => `[${l}]`).join('')}`);
    clips.forEach((c, j) => {
      const src = clips.length > 1 ? `[${labels[j]}]` : `[${idx}:a]`;
      const out = `voice${k++}`;
      const at = ms(c.atMs);
      parts.push(
        `${src}atrim=start=${sec(c.fromMs)}:end=${sec(c.toMs)},asetpts=PTS-STARTPTS,${FORMAT}${voiceVolume},adelay=${at}|${at}[${out}]`,
      );
      buses.push(`[${out}]`);
    });
  }

  // jingles
  input.jingles.forEach((j, i) => {
    const idx = n++;
    inputArgs.push('-i', j.file);
    const at = ms(j.atMs);
    // plafonné (fondu de sortie) : jamais plus long que MIX_JINGLE_MAX_MS
    const play = jinglePlayMs(j.durationMs);
    const cut =
      j.durationMs > play
        ? `atrim=0:${sec(play)},asetpts=PTS-STARTPTS,afade=t=out:st=${sec(play - MIX_JINGLE_FADE_MS)}:d=${sec(MIX_JINGLE_FADE_MS)},`
        : '';
    parts.push(`[${idx}:a]${cut}${FORMAT},volume=${round3(j.gain)},adelay=${at}|${at}[jingle${i}]`);
    buses.push(`[jingle${i}]`);
  });

  // musique par phase : un morceau en boucle par phase, fondus enchaînés, puis
  // la même enveloppe (montée, jingles, générique) sur l'ensemble
  const pieces = input.musicPhases?.length
    ? musicPieces({
        durationMs: input.durationMs,
        music: input.music,
        phases: input.musicPhases,
      })
    : null;
  if (pieces) {
    // toutes les phases sans musique : rien
    if (pieces.length > 0) {
      const env = musicEnvelope({
        gain: 1,
        durationMs: input.durationMs,
        jingles: input.jingles.map((j) => ({
          atMs: j.atMs,
          durationMs: jinglePlayMs(j.durationMs),
        })),
        outroMs: input.outroMs,
      });
      const half = MIX_MUSIC_CROSSFADE_MS / 2;
      const labels: string[] = [];
      pieces.forEach((p, i) => {
        const idx = n++;
        inputArgs.push('-stream_loop', '-1', '-i', p.file);
        const fadeIn = p.fromMs > 0;
        const fadeOut = p.toMs < input.durationMs;
        const from = fadeIn ? Math.max(0, p.fromMs - half) : 0;
        const to = fadeOut ? Math.min(input.durationMs, p.toMs + half) : input.durationMs;
        const len = to - from;
        const xf = Math.min(MIX_MUSIC_CROSSFADE_MS, len / 2);
        const fades = [
          fadeIn ? `afade=t=in:st=0:d=${sec(xf)}` : null,
          fadeOut ? `afade=t=out:st=${sec(len - xf)}:d=${sec(xf)}` : null,
        ].filter(Boolean);
        const at = ms(from);
        parts.push(
          `[${idx}:a]${FORMAT},atrim=0:${sec(len)},asetpts=PTS-STARTPTS,` +
            `${fades.map((f) => `${f},`).join('')}volume=${round3(p.gain)},adelay=${at}|${at}[mp${i}]`,
        );
        labels.push(`[mp${i}]`);
      });
      const bus =
        labels.length > 1
          ? `${labels.join('')}amix=inputs=${labels.length}:normalize=0:duration=longest:dropout_transition=0,`
          : labels[0]!;
      parts.push(
        `${bus}apad,atrim=0:${durS},asetpts=PTS-STARTPTS,volume='${envelopeExpr(env)}':eval=frame[music]`,
      );
      buses.push('[music]');
    }
  } else if (input.music && input.music.gain > 0) {
    const idx = n++;
    inputArgs.push('-stream_loop', '-1', '-i', input.music.file);
    const env = musicEnvelope({
      gain: input.music.gain,
      durationMs: input.durationMs,
      jingles: input.jingles.map((j) => ({ atMs: j.atMs, durationMs: jinglePlayMs(j.durationMs) })),
      outroMs: input.outroMs,
    });
    parts.push(
      `[${idx}:a]${FORMAT},atrim=0:${durS},asetpts=PTS-STARTPTS,volume='${envelopeExpr(env)}':eval=frame[music]`,
    );
    buses.push('[music]');
  }

  // silence de la durée de la vidéo : la piste fait toujours la bonne longueur
  parts.push(`anullsrc=r=${MIX_SAMPLE_RATE}:cl=stereo,atrim=0:${durS}[silence]`);
  buses.push('[silence]');

  parts.push(
    `${buses.join('')}amix=inputs=${buses.length}:normalize=0:duration=longest:dropout_transition=0,` +
      // format imposé avant ET après chaque étape : FFmpeg 6.0 (ffmpeg-static du
      // worker) ne sait pas négocier seul la disposition des canaux après loudnorm
      `atrim=0:${durS},${FINAL},loudnorm=I=-16:TP=-1.5:LRA=11,aresample=${MIX_SAMPLE_RATE},${FINAL},` +
      `alimiter=limit=0.95,${FINAL}[aout]`,
  );
  return { inputArgs, inputCount: n, filterComplex: parts.join(';'), outLabel: '[aout]' };
}

/** Arguments FFmpeg : images (vidéo sans son) + piste audio → master.mp4. */
export function animationMuxArgs(input: {
  videoFile: string;
  mix: AudioMix;
  outFile: string;
  durationMs: number;
}): string[] {
  // entrées du mixage d'abord (leurs numéros commencent à 0), la vidéo ensuite
  return [
    '-y',
    ...input.mix.inputArgs,
    '-i',
    input.videoFile,
    '-filter_complex',
    input.mix.filterComplex,
    '-map',
    `${input.mix.inputCount}:v`,
    '-map',
    input.mix.outLabel,
    '-c:v',
    'copy',
    '-c:a',
    'aac',
    '-b:a',
    '192k',
    '-ar',
    String(MIX_SAMPLE_RATE),
    '-t',
    sec(input.durationMs),
    '-movflags',
    '+faststart',
    input.outFile,
  ];
}

/** Arguments FFmpeg : images JPEG lues sur l'entrée standard → vidéo H.264 sans son. */
export function animationFramesArgs(input: {
  fps: number;
  width: number;
  height: number;
  outFile: string;
  crf: number;
  preset: string;
}): string[] {
  return [
    '-y',
    '-f',
    'image2pipe',
    '-framerate',
    String(input.fps),
    '-c:v',
    'mjpeg',
    '-i',
    '-',
    '-vf',
    `scale=${input.width}:${input.height}:flags=lanczos,format=yuv420p`,
    '-c:v',
    'libx264',
    '-preset',
    input.preset,
    '-crf',
    String(input.crf),
    '-r',
    String(input.fps),
    '-pix_fmt',
    'yuv420p',
    input.outFile,
  ];
}

/** Morceaux de voix du plan avec le fichier local de chaque voix. */
export function voiceClipsOf(
  plan: Pick<AnimationCapturePlan, 'clips'>,
  fileOf: (path: string) => string | undefined,
): AudioMixInput['voices'] {
  const out: { file: string; fromMs: number; toMs: number; atMs: number }[] = [];
  for (const c of plan.clips) {
    const file = fileOf(c.path);
    if (file) out.push({ file, fromMs: c.fromMs, toMs: c.toMs, atMs: c.atMs });
  }
  return out;
}
